/**
 * @file NewPatientView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import { Avatar, Button, Grid, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { Alert, Box } from "@mui/material";
import { Container } from "@mui/system";
import { ThemeProvider } from "@mui/material/styles";
import React from "react";
import { Navigate } from "react-router-dom";
import { ApiError } from "../../../../services/models/ApiError";
import { NewPatientViewModel } from "../viewmodels/NewPatientViewModel";
import { BackDropLoadingView } from "../../../utils/BackDropLoadingView";
import { GenerateButton, Identitier, SubTitle, Tittle } from "../themes/AddPatientDialogTheme";
import "../../../common/css/Common.css";
import "../views/PatientList.css";
import { BaseComponent } from "../../base/BaseComponent";
import { Clinic } from "../../../../services/models/Clinic";
import { CreatePatientResponse } from "../../../../services/models/CreatePatientResponse";
import { LocationComponent } from "../../../utils/LocationComponent";
import { RoleType } from "../../../login/utils/RoleType";

/**
 * A type of Landing state
 */
type CompState = {
    errorToggle: boolean, backDropLoading: boolean, submitted: boolean, errorMessage: string,
    identifier: string, clinicId: string
};

/**
 * A type of Component state
 */
interface Props {
    onDialogDismissed: any,
    onNewPatientCreated: any,
    viewModel: NewPatientViewModel
}

/**
 * A class definition for NewPatientView
 */  
export class NewPatientView extends BaseComponent<Props, CompState> {

    location?: Location;
    isIdentifierDisable = true;
    constructor(props: Props) {
        super(props)
        const role = this.getLoggedInUserRole();
        this.logger.verbose(`NewPatientView(${JSON.stringify(role)})`, NewPatientView.name);
        this.isIdentifierDisable = !(this.getLoggedInUserRole() && this.getLoggedInUserRole().includes(RoleType.HCP));

        const clinic = this.getSelectedClinic(); 
        let selectedClinic: Clinic = new Clinic();
  
        if(!this.isEmpty(clinic)){
           selectedClinic = JSON.parse(clinic) as Clinic; // eslint-disable-line
           this.logger.verbose(`Current Clinic - ${JSON.stringify(selectedClinic.name)}`, NewPatientView.name); 
        }

        this.state = {
            errorToggle: false, backDropLoading: false,
            submitted: false, errorMessage: "",
            identifier: "", clinicId: selectedClinic.id !! // eslint-disable-line
        };
    }

    /**
     * Dismisses the dialog
     */
    private dismissDialog = () => {
        this.props.onDialogDismissed();
    }

    /**
     * On identifier changes
     * @param newIdentifier string
     */
    private onIdentifierchange = (newIdentifier: string) => {
        this.setState({
            identifier: newIdentifier.trim()
        });
    }

    /**
     * Hides loading
     * @param message string
     */
    private hideLoading = (message: string)=> {
        this.setState({
            errorMessage: message,
            errorToggle: true,
            backDropLoading: false
        });
    }

    /**
     * Shows loading
     */
    private showLoading = ()=>{
        this.setState({
            errorToggle: false,
            backDropLoading: true
        })
    }

    /**
     * Activates patient
     * @returns 
     */
    private activatePatient = async () => {
            const accessToken = this.validateAndGetSession(NewPatientView.name);
            this.showLoading();
            this.props.viewModel.onPatientCreated.subscribe((response: CreatePatientResponse)=>{
                this.setState({
                    errorToggle: false,
                    backDropLoading: false,
                });
                this.logger.verbose(`Patient Activated - ${response.message}`, NewPatientView.name)
                this.props.onNewPatientCreated(response.data.id)
                this.props.onDialogDismissed();
            });

            this.props.viewModel.onPatientCreatedError.subscribe((apiError: ApiError)=>{
                this.hideLoading(`${apiError.message}`);
            });

            if(this.state.identifier.trim().length> 0) {
              this.props.viewModel.createPatient(this.state.clinicId, accessToken, this.state.identifier.trim());
              return;
            } 

            this.props.viewModel.createPatient(this.state.clinicId, accessToken);
    }

    /**
     * Handles backdrop close
     */
    private handleBackDropClose = () => this.setState({ backDropLoading: false});
    
    /**
     * Handles location updates
     * @param location Location
     * @returns 
     */
    private handleLocationUpdate = (location: Location) => this.location = location;
    

    useQuery = () => new URLSearchParams(this.location?.search);

    render() {

        return <>
            <div>
                {<LocationComponent onChange={this.handleLocationUpdate} />}
                {this.state.submitted && <Navigate to="/home" replace={true}></Navigate>}
                <Box
                    sx={{
                        display: "flex",
                        "& > :not(style)": {
                            m: 1,
                            width: "100%"
                        },
                    }}>
                    <Paper className="AddPatientRecord" elevation={0}>
                        <Grid container spacing={0}>
                            <Grid item xs={9} className="AddPatientRecordHeader">
                                <ThemeProvider theme={Tittle}>
                                    <Typography className="NewDialogTitle">
                                        Add a Patient Record
                                    </Typography>
                                </ThemeProvider >
                            </Grid>
                            <Grid item xs={3} container justifyContent="flex-end">
                            <IconButton className="CloseButton" onClick={this.dismissDialog}><Avatar src={"/../closeIcon.svg"} /></IconButton>                            
                        </Grid>
                        </Grid>
                        <ThemeProvider theme={SubTitle}>
                            <Typography className="NewDialogSubTitle">
                                Enter an identifier below or just click on Generate QR Code Button
                            </Typography>
                        </ThemeProvider>
                        <Container>
                            <ThemeProvider theme={Identitier}>
                                <Typography className="Identifier">
                                    Identitier (Optional)
                                </Typography>
                            </ThemeProvider>
                            <TextField fullWidth id="outlined-basic" label="Enter your identifier" variant="outlined"
                                value={this.state.identifier} disabled= {this.isIdentifierDisable}
                                onChange={(e) => { this.onIdentifierchange(e.target.value) }} className="IdentifierTextField" />
                                <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                        alignItems="flex-end"
                                        spacing={2}
                                        >
                                        <ThemeProvider theme={GenerateButton}>
                                            <Button variant="contained" onClick={this.dismissDialog} size="small"
                                                disableElevation className="CancelNewDialogBtn">
                                                Cancel
                                            </Button>
                                        </ThemeProvider>
                                        <ThemeProvider theme={GenerateButton}>
                                            <Button variant="contained" onClick={this.activatePatient} size="small"
                                                disableElevation className="ActivateNewUserBtn">
                                                Generate QR Code
                                            </Button>
                                    </ThemeProvider>
                            </Stack>
                        </Container>
                            
                    </Paper>
                </Box>
                <BackDropLoadingView handleBackDropClose={this.handleBackDropClose} isLoading={this.state.backDropLoading} />
                {
                    this.state.errorToggle && <Alert severity="error">
                        {this.state.errorMessage}
                    </Alert>
                }
            </div>
        </>
    }
}